import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import { useContext, useRoute } from '@nuxtjs/composition-api';
import CustomExchangePromoCode from '~/components/Items/CustomExchangePromoCode.vue';
import ResponsiveDialog from '~/components/ResponsiveDialog.vue';
import { useSanityDocument } from '~/composables/useSanityDocument';
import { useStore } from '~/store/composable';
import {
  TPromptToRegisterDispatch,
  TPromptToLoginDispatch,
  ThemeName,
} from '~/store/types';
import { EExchangeState } from '~/store/customReveal/types';
import { IRedemptionState } from '~/types/promo-box';
import {
  ISanityDocuments,
  SanityDocumentSchema,
} from '~/types/sanity-documents';
import PromoBox from './PromoBox.vue';
import PromoBoxExplainer from './PromoBoxExplainer.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'PromoBoxExchange',
  props: {
    closeBtn: { type: Boolean, default: true },
    doc: null,
    loading: { type: Boolean },
    titleTag: { default: 'h1' }
  },
  setup(__props: any) {

const props = __props as { closeBtn: boolean, doc?: ISanityDocuments[SanityDocumentSchema.PROMO_BOX], loading: boolean, titleTag: 'h1' | 'h2' };

const schema = SanityDocumentSchema.PROMO_BOX;



const validateRedemptionPeriod = () => {
  const now = new Date();
  const start = props.doc?.startsAt ? new Date(props.doc.startsAt) : undefined;
  const end = props.doc?.endsAt ? new Date(props.doc.endsAt) : undefined;
  return (!start || now >= start) && (!end || now <= end);
};

const exchangeComponent = ref<InstanceType<
  typeof CustomExchangePromoCode
> | null>();
const { $sanityImage, $sanityLookup } = useContext();
const route = useRoute();
const { commit, dispatch, getters, state } = useStore();
const currentState = ref(IRedemptionState.REDEEMABLE);
const currentExchangeState = computed(
  () => state?.customReveal?.currentExchangeState,
);

const canRedeem = ref(false);
const canUserRedeem = ref(true);
const isAutoStart = computed(
  () => route.value?.query && 'started' in route.value?.query,
);
const isRedeeming = ref(false);
const isWithinRedemptionPeriod = computed(() => {
  if (!props.doc?.startsAt && !props.doc?.endsAt) {
    return true;
  }
  return validateRedemptionPeriod();
});
const shouldShowCustomExchangeReveal = ref(false);
const shouldShowExplainerDialog = ref(false);
const toolbarError = ref('');

// temp mock data
const mock = computed<
  | 'success'
  | 'restrictionsError'
  | 'userRedeemedError'
  | 'exchangeError'
  | undefined
>(() => {
  switch (props.doc?.slug) {
    case 'mocksuccess':
      return 'success';
    case 'mockrestrictionserror':
      return 'restrictionsError';
    case 'mockuserredeemederror':
      return 'userRedeemedError';
    case 'mockexchangeerror':
      return 'exchangeError';
  }
});

const { doc: authConfig } = useSanityDocument<
  ISanityDocuments[SanityDocumentSchema.AUTH_CONFIG]
>(
  computed(() => ({
    _id: props.doc?.auth?._ref,
    _type: SanityDocumentSchema.AUTH_CONFIG,
  })),
);

const redirectPath = computed(() => {
  const basePath =
    route.value?.fullPath ||
    (props.doc?.slug
      ? $sanityLookup.getPageRoutePath({
          slug: props.doc.slug,
          schema,
        })
      : '/');
  if (!basePath) {
    return undefined;
  }
  if (isAutoStart.value) {
    return basePath;
  }
  return `${basePath}${basePath?.indexOf('?') >= 0 ? '&' : '?'}started`;
});

const login = () => {
  dispatch<TPromptToLoginDispatch>({
    type: 'promptToLogin',
    theme: authConfig?.value?.theme,
    redirectAfterLoginPath: redirectPath.value,
  });
};

const register = () => {
  dispatch<TPromptToRegisterDispatch>({
    type: 'promptToRegister',
    theme: authConfig?.value?.theme,
    redirectAfterLoginPath: redirectPath.value,
    activeCampaignId: props.doc?.activeCampaignId,
  });
};

const resetLocalExchangeState = () => {
  shouldShowCustomExchangeReveal.value = false;
  isRedeeming.value = false;
};

const isLoggedIn = computed(() => {
  return state.profile?.user?.loggedIn ?? false;
});

const handleRedeem = async () => {
  if (currentState.value !== IRedemptionState.REDEEMABLE) {
    return;
  }

  if (!validateRedemptionPeriod()) {
    handleExpired(true);
    return;
  }

  if (!isLoggedIn.value) {
    login();
    return;
  }

  isRedeeming.value = true;

  if (currentExchangeState.value === EExchangeState.NOT_EXCHANGING) {
    exchangeComponent.value?.handleExchange();
  }

  if (currentExchangeState.value === EExchangeState.EXCHANGE_COMPLETE) {
    shouldShowCustomExchangeReveal.value = true;
  }
};

const handleRedeemed = () => {
  currentState.value = IRedemptionState.REDEEMED;
  resetLocalExchangeState();
};

const handleExchangeClosed = () => {
  if (!!getters['customReveal/areCardsRevealed']) {
    currentState.value = IRedemptionState.REDEEMED;
  }
  resetLocalExchangeState();
};

const handleExchangeError = (message?: string) => {
  resetLocalExchangeState();
  if (message) {
    commit('updateSnackbarErrorText', message);
    commit('toggleErrorSnackbar');
  }
};

const handleUserQualified = (isQualified: boolean, message?: string) => {
  if (!isQualified) {
    resetLocalExchangeState();
    canUserRedeem.value = false;
    if (message) {
      toolbarError.value = message;
    }
  }
};

const handleExplainerCtaClick = () => {
  if (!isLoggedIn.value) {
    register();
    return;
  }
  if (
    currentState.value === IRedemptionState.REDEEMABLE &&
    canRedeem.value &&
    canUserRedeem.value
  ) {
    handleRedeem();
    shouldShowExplainerDialog.value = false;
  }
};

const handleExpired = (isExpired: boolean) => {
  if (currentState.value === IRedemptionState.REDEEMED) {
    return;
  }
  if (isExpired) {
    currentState.value = IRedemptionState.EXPIRED;
  } else {
    currentState.value = IRedemptionState.REDEEMABLE;
  }
};

watch(
  [isAutoStart, isLoggedIn, canRedeem],
  ([currentIsAutostart, currentIsLoggedIn, currentCanRedeem]) => {
    if (currentIsAutostart && currentIsLoggedIn && currentCanRedeem) {
      handleRedeem();
    }
  },
  { immediate: true },
);

watch(
  exchangeComponent,
  (current) => {
    if (!!current?.handleExchange && state.customReveal?.videoReady === true) {
      canRedeem.value = true;
    }
  },
  { immediate: true },
);

watch(
  () => state.customReveal?.videoReady,
  (current) => {
    if (current === true && !!exchangeComponent.value?.handleExchange) {
      canRedeem.value = true;
    }
  },
  { immediate: true },
);

watch(currentExchangeState, (current, previous) => {
  if (
    previous === EExchangeState.EXCHANGE_IN_FLIGHT &&
    current === EExchangeState.EXCHANGE_COMPLETE
  ) {
    shouldShowCustomExchangeReveal.value = true;
  }
});

watch(
  [() => props.doc?.isRedeemable, isWithinRedemptionPeriod],
  ([currentIsRedeemable, currentWithinRedemptionPeriod]) => {
    handleExpired(!(currentIsRedeemable && currentWithinRedemptionPeriod));
  },
  { immediate: true },
);

return { __sfc: true,schema, props, validateRedemptionPeriod, exchangeComponent, $sanityImage, $sanityLookup, route, commit, dispatch, getters, state, currentState, currentExchangeState, canRedeem, canUserRedeem, isAutoStart, isRedeeming, isWithinRedemptionPeriod, shouldShowCustomExchangeReveal, shouldShowExplainerDialog, toolbarError, mock, authConfig, redirectPath, login, register, resetLocalExchangeState, isLoggedIn, handleRedeem, handleRedeemed, handleExchangeClosed, handleExchangeError, handleUserQualified, handleExplainerCtaClick, handleExpired, CustomExchangePromoCode, ResponsiveDialog, ThemeName, EExchangeState, IRedemptionState, PromoBox, PromoBoxExplainer }
}

})